import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, apiGet } from "../../api";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Download, CheckCircle2, Boxes, Zap, AlertTriangle } from "lucide-react";
import { Badge, CURRENCY_SYMBOLS, Modal, StatusPill, fmtPrice } from "./shared";

/** Submissions tab: filterable approval queue + per-row Review modal.
 * `focusId` (from a ?sub=<id> deep link, e.g. the manager digest) opens that
 * submission's Review modal straight away. */
export default function SubmissionsTab({ focusId = null }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [confirmationFilter, setConfirmationFilter] = useState("all");
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  // Open the deep-linked submission once on mount. The Review modal fetches the
  // submission by id itself, so it works even if the row isn't in the current filter.
  useEffect(() => {
    if (focusId) setSelected(focusId);
  }, [focusId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet("/portal/admin/submissions", { params: { status: statusFilter, supplier_id: supplierFilter, confirmation: confirmationFilter, limit: 200 } });
      setItems(data?.submissions || []);
    } catch (err) {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, supplierFilter, confirmationFilter]);

  useEffect(() => {
    apiGet("/portal/admin/suppliers").then(d => setSuppliers(d?.suppliers || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="card" data-testid="submissions-tab">
      <div style={{ display: "flex", gap: "1rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
        <div style={{ minWidth: "180px" }}>
          <label className="form-label">Status</label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="auto_approved">Auto-approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div style={{ minWidth: "200px" }}>
          <label className="form-label">Supplier</label>
          <Select value={supplierFilter} onValueChange={setSupplierFilter}>
            <SelectTrigger data-testid="filter-supplier"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All suppliers</SelectItem>
              {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div style={{ minWidth: "210px" }}>
          <label className="form-label">System confirmation</label>
          <Select value={confirmationFilter} onValueChange={setConfirmationFilter}>
            <SelectTrigger data-testid="filter-confirmation"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending (WMS or Kraken)</SelectItem>
              <SelectItem value="done">Fully confirmed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="data-table" data-testid="submissions-table">
          <thead>
            <tr>
              <th>Reference</th><th>Supplier</th><th>Category</th><th>Type</th>
              <th style={{ textAlign: "center" }}>Cur</th>
              <th style={{ textAlign: "center" }}>SKUs</th>
              <th>Submitted</th><th>Status</th>
              <th style={{ textAlign: "center" }}>WMS</th>
              <th style={{ textAlign: "center" }}>Kraken</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} style={{ textAlign: "center", padding: "2rem", opacity: 0.6 }}>Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={11} style={{ textAlign: "center", padding: "2rem", opacity: 0.6 }}>No submissions match the current filters.</td></tr>
            ) : items.map(s => (
              <tr key={s.id} data-testid={`submission-row-${s.id}`}>
                <td style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{s.id.slice(0, 8)}</td>
                <td>{s.supplier_name}</td>
                <td>{s.supplier_category}</td>
                <td>{s.type === "manual" ? "Manual" : "Bulk"}</td>
                <td style={{ textAlign: "center", fontFamily: "monospace", fontSize: "0.78rem", color: (s.currency && s.currency !== "GBP") ? "#FFD84F" : "var(--octopus-voltage)" }} title={s.currency || "GBP"}>
                  {(s.currency || "GBP")}
                </td>
                <td style={{ textAlign: "center" }}>{s.sku_count}</td>
                <td>{new Date(s.submitted_at).toLocaleString()}</td>
                <td><StatusPill status={s.status} reviewStatus={s.review_status} /></td>
                <td style={{ textAlign: "center" }}><SystemDot ok={!!s.wms_confirmed_at} relevant={s.status === "approved" || s.status === "auto_approved"} title={s.wms_confirmed_at ? `Confirmed by ${s.wms_confirmed_by} at ${new Date(s.wms_confirmed_at).toLocaleString()}` : "Not yet confirmed"} /></td>
                <td style={{ textAlign: "center" }}><SystemDot ok={!!s.kraken_confirmed_at} relevant={s.status === "approved" || s.status === "auto_approved"} title={s.kraken_confirmed_at ? `Confirmed by ${s.kraken_confirmed_by} at ${new Date(s.kraken_confirmed_at).toLocaleString()}` : "Not yet confirmed"} /></td>
                <td>
                  <Button onClick={() => setSelected(s.id)} className="btn btn-secondary" data-testid={`review-btn-${s.id}`} style={{ padding: "0.3rem 0.7rem", fontSize: "0.85rem" }}>Review</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <ReviewModal submissionId={selected} onClose={(changed = false) => { setSelected(null); if (changed === true) load(); }} />}
    </div>
  );
}


/** Small status dot for the WMS / Kraken columns in the submissions table.
 *  Greyed out when the submission isn't approved (those confirmations aren't
 *  applicable yet); green dot when confirmed; hollow ring when pending. */
function SystemDot({ ok, relevant, title }) {
  if (!relevant) {
    return <span title="Not yet approved" style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "rgba(255,255,255,0.1)" }} />;
  }
  return (
    <span
      title={title}
      style={{
        display: "inline-block", width: 10, height: 10, borderRadius: "50%",
        background: ok ? "#22c55e" : "transparent",
        border: ok ? "none" : "1.5px solid rgba(255,216,79,0.8)",
        boxShadow: ok ? "0 0 6px rgba(34,197,94,0.6)" : "none",
      }}
    />
  );
}


/** ±X% pill that mirrors the email design — red for increases, green for
 *  decreases, indigo "NEW" for SKUs not previously on file. */
function DeltaBadge({ pct, isNew }) {
  if (isNew) {
    return <Badge bg="rgba(99,102,241,0.18)" fg="#a5b4fc" border="#6366f1" label="NEW" />;
  }
  if (pct == null) {
    return <Badge bg="rgba(255,255,255,0.05)" fg="#9ca3af" border="rgba(255,255,255,0.15)" label="—" />;
  }
  if (Math.abs(pct) < 0.05) {
    return <Badge bg="rgba(255,255,255,0.05)" fg="#9ca3af" border="rgba(255,255,255,0.15)" label="no change" />;
  }
  const up = pct > 0;
  return (
    <Badge
      bg={up ? "rgba(244,63,94,0.18)" : "rgba(34,197,94,0.18)"}
      fg={up ? "#fda4af" : "#86efac"}
      border={up ? "#f43f5e" : "#22c55e"}
      label={`${up ? "▲" : "▼"} ${Math.abs(pct).toFixed(1)}%`}
    />
  );
}


/** Classify a row for filtering. Matches the email's thresholds. */
function rowKind(r) {
  const pct = r.pct_change;
  if (pct == null && r.old_price == null) return "new";
  if (pct != null && pct > 0.05) return "inc";
  if (pct != null && pct < -0.05) return "dec";
  return "flat";
}

/** Aggregate stats for the summary strip — counts plus avg/largest moves. */
function computeRowStats(rows) {
  const s = { total: 0, inc: 0, dec: 0, fresh: 0, flat: 0, avgInc: 0, maxInc: null, maxDec: null };
  let incSum = 0;
  for (const r of rows || []) {
    s.total++;
    const kind = rowKind(r);
    if (kind === "new") s.fresh++;
    else if (kind === "inc") {
      s.inc++; incSum += r.pct_change;
      if (!s.maxInc || r.pct_change > s.maxInc.pct) s.maxInc = { pct: r.pct_change, sku: r.sku };
    } else if (kind === "dec") {
      s.dec++;
      if (!s.maxDec || r.pct_change < s.maxDec.pct) s.maxDec = { pct: r.pct_change, sku: r.sku };
    } else s.flat++;
  }
  s.avgInc = s.inc ? incSum / s.inc : 0;
  return s;
}

const ROWS_PAGE_SIZE = 150;

/** Clickable count chip used as a row filter in the review modal. */
function FilterChip({ label, count, color, active, onClick, testId }) {
  if (count === 0 && label !== "All") return null;
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      style={{
        padding: "0.25rem 0.7rem", borderRadius: "999px", cursor: "pointer",
        fontSize: "0.78rem", fontWeight: 600,
        background: active ? "rgba(96,240,248,0.15)" : "rgba(255,255,255,0.04)",
        color: color || "var(--octopus-ice)",
        border: `1px solid ${active ? "rgba(96,240,248,0.6)" : "rgba(255,255,255,0.12)"}`,
      }}
    >
      {label} <span style={{ opacity: 0.75 }}>({count})</span>
    </button>
  );
}


function ReviewModal({ submissionId, onClose }) {
  const [data, setData] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [rowFilter, setRowFilter] = useState("all");
  const [sortByDelta, setSortByDelta] = useState(false);
  const [visibleCount, setVisibleCount] = useState(ROWS_PAGE_SIZE);
  const [cpi, setCpi] = useState(null); // projected cost-of-install impact (null if no BOM loaded)

  useEffect(() => {
    apiGet(`/portal/admin/submissions/${submissionId}`).then(setData).catch(() => toast.error("Failed to load submission"));
  }, [submissionId]);

  // Run this submission's prices through the QBR modeller → projected cost-per-install
  // impact. Best-effort: returns null when no BOM is loaded, so the banner just hides.
  useEffect(() => {
    apiGet(`/qbr/submission-impact/${submissionId}`, { optional: true }).then((d) => setCpi(d && d.overall ? d : null)).catch(() => setCpi(null));
  }, [submissionId]);

  const stats = useMemo(() => computeRowStats(data?.rows), [data]);

  // Filtered + optionally delta-sorted view of the rows. Keys use the row's
  // original index so React identity is stable across filter/sort changes.
  const filteredRows = useMemo(() => {
    let rows = (data?.rows || []).map((r, i) => ({ ...r, _i: i }));
    if (rowFilter !== "all") rows = rows.filter(r => rowKind(r) === rowFilter);
    if (sortByDelta) {
      rows = [...rows].sort((a, b) => Math.abs(b.pct_change ?? 0) - Math.abs(a.pct_change ?? 0));
    }
    return rows;
  }, [data, rowFilter, sortByDelta]);

  const setFilter = (f) => { setRowFilter(f); setVisibleCount(ROWS_PAGE_SIZE); };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/portal/admin/submissions/${submissionId}/review`, { action: "approve" });
      const notif = res.data?.notification;
      if (res.data?.review_queued) {
        toast.warning("Approved — held in SKU review queue. Unrecognised SKUs need reconciliation before prices are written.");
      } else if (notif && notif.sent === false) {
        toast.warning(`Approved, but post-approval email did not send: ${notif.reason || "unknown"}`);
      } else {
        toast.success("Submission approved");
      }
      onClose(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approve failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error("A reason is required to reject"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/portal/admin/submissions/${submissionId}/review`, { action: "reject", reason: rejectReason.trim() });
      toast.success("Submission rejected");
      onClose(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reject failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) return <Modal onClose={onClose}><p>Loading…</p></Modal>;

  const cur = data.currency || "GBP";
  const curSym = CURRENCY_SYMBOLS[cur] || "£";

  return (
    <Modal onClose={onClose}>
      <div data-testid="review-modal" style={{ maxWidth: "900px" }}>
        <div style={{ marginBottom: "1rem" }}>
          <h2 style={{ fontSize: "1.2rem", marginBottom: "0.3rem", color: "var(--octopus-ice)" }}>
            {data.supplier_name} <StatusPill status={data.status} reviewStatus={data.review_status} />
            <span data-testid="review-currency-pill" style={{
              marginLeft: "0.5rem", padding: "0.15rem 0.55rem", borderRadius: "999px",
              background: "rgba(96,240,248,0.12)", color: "var(--octopus-ice)",
              fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.04em",
              border: "1px solid rgba(96,240,248,0.3)", textTransform: "uppercase",
            }}>{curSym} {cur}</span>
          </h2>
          <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", margin: 0 }}>
            {data.supplier_category} · {data.type === "manual" ? "Manual entry" : "Bulk upload"} · {new Date(data.submitted_at).toLocaleString()}
          </p>
          {data.original_filename && <p style={{ color: "var(--octopus-voltage)", fontSize: "0.8rem", opacity: 0.7, margin: "0.3rem 0 0" }}>File: {data.original_filename}</p>}
        </div>

        {data.notes && (
          <div style={{ padding: "0.6rem 0.85rem", background: "rgba(96,240,248,0.06)", borderRadius: "6px", marginBottom: "1rem", fontSize: "0.85rem" }}>
            <strong style={{ color: "var(--octopus-ice)" }}>Notes:</strong> <span style={{ color: "var(--octopus-voltage)" }}>{data.notes}</span>
          </div>
        )}

        {/* At-a-glance impact strip — mirrors the email "At a glance" block.
            Counts double as filters; avg/largest stats let a 3,000-row file
            be judged without reading the table. */}
        <div data-testid="review-impact-strip" style={{ padding: "0.65rem 0.9rem", background: "rgba(10,18,42,0.5)", border: "1px solid rgba(96,240,248,0.15)", borderRadius: "6px", marginBottom: "1rem", fontSize: "0.85rem" }}>
          <span style={{ color: "var(--octopus-voltage)", opacity: 0.7, fontSize: "0.7rem", letterSpacing: "0.06em", textTransform: "uppercase", display: "block", marginBottom: "0.4rem" }}>At a glance</span>
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginBottom: (stats.inc || stats.dec) ? "0.5rem" : 0 }}>
            <FilterChip label="All" count={stats.total} active={rowFilter === "all"} onClick={() => setFilter("all")} testId="row-filter-all" />
            <FilterChip label="▲ Increases" count={stats.inc} color="#fda4af" active={rowFilter === "inc"} onClick={() => setFilter("inc")} testId="row-filter-inc" />
            <FilterChip label="▼ Decreases" count={stats.dec} color="#86efac" active={rowFilter === "dec"} onClick={() => setFilter("dec")} testId="row-filter-dec" />
            <FilterChip label="New SKUs" count={stats.fresh} color="#a5b4fc" active={rowFilter === "new"} onClick={() => setFilter("new")} testId="row-filter-new" />
            <FilterChip label="No change" count={stats.flat} color="#9ca3af" active={rowFilter === "flat"} onClick={() => setFilter("flat")} testId="row-filter-flat" />
          </div>
          {(stats.inc > 0 || stats.dec > 0) && (
            <div data-testid="review-impact-stats" style={{ fontSize: "0.78rem", color: "var(--octopus-voltage)" }}>
              {stats.inc > 0 && <>avg increase <strong style={{ color: "#fda4af" }}>+{stats.avgInc.toFixed(1)}%</strong></>}
              {stats.maxInc && <> · largest <strong style={{ color: "#fda4af" }}>+{stats.maxInc.pct.toFixed(1)}%</strong> <span style={{ fontFamily: "monospace", fontSize: "0.72rem" }}>({stats.maxInc.sku})</span></>}
              {stats.maxDec && <> · largest decrease <strong style={{ color: "#86efac" }}>{stats.maxDec.pct.toFixed(1)}%</strong> <span style={{ fontFamily: "monospace", fontSize: "0.72rem" }}>({stats.maxDec.sku})</span></>}
            </div>
          )}
        </div>

        {cpi && (
          <div data-testid="review-cpi-impact" style={{ padding: "0.65rem 0.9rem", background: "rgba(168,85,247,0.08)", border: "1px solid rgba(168,85,247,0.3)", borderRadius: "6px", marginBottom: "1rem", fontSize: "0.85rem", display: "flex", alignItems: "baseline", gap: "0.6rem", flexWrap: "wrap" }}>
            <span style={{ color: "var(--octopus-voltage)", opacity: 0.7, fontSize: "0.7rem", letterSpacing: "0.06em", textTransform: "uppercase" }}>Projected cost-of-install impact</span>
            <strong style={{ fontSize: "1.05rem", color: cpi.overall.delta >= 0 ? "#fda4af" : "#86efac" }}>
              {cpi.overall.delta >= 0 ? "+" : "−"}£{Math.abs(cpi.overall.delta).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / install
            </strong>
            <span style={{ color: "var(--octopus-voltage)" }}>
              ({cpi.overall.deltaPct >= 0 ? "+" : ""}{cpi.overall.deltaPct}% · £{Math.round(cpi.overall.currentCost).toLocaleString("en-GB")} → £{Math.round(cpi.overall.newCost).toLocaleString("en-GB")})
            </span>
            <span style={{ color: "var(--octopus-voltage)", opacity: 0.6, fontSize: "0.72rem" }}>
              {cpi.drivers?.length || 0} line(s) affect installs · {cpi.basis === "actual" ? "actual (approved)" : "theoretical — modelled vs BOM"}
            </span>
          </div>
        )}

        {cpi?.uomWarnings?.length > 0 && (
          <div data-testid="review-uom-warnings" style={{ padding: "0.65rem 0.9rem", background: "rgba(245,158,11,0.09)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: "6px", marginBottom: "1rem", fontSize: "0.83rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", marginBottom: "0.4rem" }}>
              <AlertTriangle size={14} style={{ color: "#f59e0b" }} />
              <strong style={{ color: "#fbbf24" }}>Possible unit-of-measure mismatch — excluded from the impact above</strong>
            </div>
            <div style={{ color: "var(--octopus-voltage)", opacity: 0.85, fontSize: "0.76rem", marginBottom: "0.5rem" }}>
              These prices look like they may be in a different unit from what we dispatch (e.g. per pack, coil or 3m length). Check the unit before approving.
            </div>
            {cpi.uomWarnings.slice(0, 8).map((w, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: "0.6rem", padding: "0.25rem 0", borderTop: i ? "1px solid rgba(245,158,11,0.15)" : "none", fontSize: "0.78rem" }}>
                <span style={{ color: "var(--octopus-voltage)" }}><strong style={{ fontFamily: "monospace" }}>{w.part}</strong> {w.description ? `· ${w.description}` : ""}</span>
                <span style={{ color: "#fbbf24", whiteSpace: "nowrap" }}>
                  submitted £{Number(w.submitted).toLocaleString("en-GB", { maximumFractionDigits: 4 })} vs dispatched £{Number(w.dispatchUnit).toLocaleString("en-GB", { maximumFractionDigits: 4 })} (~{w.factor}×)
                </span>
              </div>
            ))}
          </div>
        )}

        {data.rows_skipped > 0 && (
          <div style={{ padding: "0.6rem 0.85rem", background: "rgba(240,80,248,0.08)", borderRadius: "6px", marginBottom: "1rem", fontSize: "0.85rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ color: "var(--octopus-voltage)" }}><strong style={{ color: "#F050F8" }}>{data.rows_skipped}</strong> rows skipped during file parsing.</span>
            <Button onClick={() => window.location.href = `${API}/portal/admin/submissions/${submissionId}/error-report`} className="btn btn-secondary" data-testid="admin-download-error-report" style={{ padding: "0.3rem 0.7rem", fontSize: "0.8rem" }}>
              <Download size={12} style={{ marginRight: "0.3rem" }} /> Error report
            </Button>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
          <span style={{ fontSize: "0.78rem", color: "var(--octopus-voltage)", opacity: 0.8 }}>
            {filteredRows.length === stats.total
              ? `${stats.total.toLocaleString()} row${stats.total !== 1 ? "s" : ""}`
              : `${filteredRows.length.toLocaleString()} of ${stats.total.toLocaleString()} rows`}
          </span>
          <button
            onClick={() => { setSortByDelta(s => !s); setVisibleCount(ROWS_PAGE_SIZE); }}
            data-testid="sort-by-delta-toggle"
            style={{
              padding: "0.2rem 0.6rem", borderRadius: "6px", cursor: "pointer", fontSize: "0.75rem",
              background: sortByDelta ? "rgba(96,240,248,0.12)" : "transparent",
              color: "var(--octopus-ice)", border: "1px solid rgba(96,240,248,0.25)",
            }}
          >
            Sort: {sortByDelta ? "Biggest change" : "File order"}
          </button>
        </div>
        <div style={{ maxHeight: "350px", overflowY: "auto", border: "1px solid rgba(96,240,248,0.15)", borderRadius: "6px", marginBottom: "1rem" }}>
          <table className="data-table" data-testid="review-rows-table" style={{ marginBottom: 0 }}>
            <thead style={{ position: "sticky", top: 0, background: "rgba(10,18,42,0.98)" }}>
              <tr>
                <th>SKU</th>
                <th>Description</th>
                <th style={{ textAlign: "right" }}>Old ({cur})</th>
                <th style={{ textAlign: "right" }}>New ({cur})</th>
                <th style={{ textAlign: "center" }}>Change</th>
                <th>Effective From</th>
                <th>Effective To</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.slice(0, visibleCount).map((r) => (
                <tr key={r._i}>
                  <td style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>{r.sku}</td>
                  <td>{r.product_description || "—"}</td>
                  <td style={{ textAlign: "right", fontFamily: "monospace", color: r.old_price == null ? "var(--octopus-voltage)" : "var(--octopus-ice)", opacity: r.old_price == null ? 0.6 : 1 }}>
                    {r.old_price == null ? "—" : fmtPrice(r.old_price, cur)}
                  </td>
                  <td style={{ textAlign: "right", fontFamily: "monospace", fontWeight: 600 }}>{fmtPrice(r.new_price, cur)}</td>
                  <td style={{ textAlign: "center" }}>
                    <DeltaBadge pct={r.pct_change} isNew={r.pct_change == null && r.old_price == null} />
                  </td>
                  <td>{r.effective_date}</td>
                  <td style={{ color: r.effective_to ? "var(--octopus-ice)" : "var(--octopus-voltage)", fontStyle: r.effective_to ? "normal" : "italic" }}>
                    {r.effective_to || "Open-ended"}
                  </td>
                </tr>
              ))}
              {filteredRows.length > visibleCount && (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: "0.6rem" }}>
                    <button
                      onClick={() => setVisibleCount(c => c + ROWS_PAGE_SIZE)}
                      data-testid="show-more-rows"
                      style={{
                        padding: "0.35rem 1rem", borderRadius: "6px", cursor: "pointer", fontSize: "0.8rem",
                        background: "rgba(96,240,248,0.08)", color: "var(--octopus-ice)",
                        border: "1px solid rgba(96,240,248,0.25)", fontWeight: 600,
                      }}
                    >
                      Show {Math.min(ROWS_PAGE_SIZE, filteredRows.length - visibleCount).toLocaleString()} more
                      ({(filteredRows.length - visibleCount).toLocaleString()} remaining)
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {data.status === "pending" ? (
          <div style={{ borderTop: "1px solid rgba(96,240,248,0.15)", paddingTop: "1rem" }}>
            <label className="form-label" style={{ fontSize: "0.8rem" }}>Reject reason (required to reject)</label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={2}
              data-testid="reject-reason"
              style={{ width: "100%", padding: "0.5rem 0.75rem", borderRadius: "6px", background: "rgba(10,18,42,0.6)", border: "1px solid rgba(96,240,248,0.2)", color: "var(--octopus-ice)", marginBottom: "0.75rem" }}
            />
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
              <Button onClick={onClose} className="btn btn-secondary" disabled={submitting}>Cancel</Button>
              <Button onClick={handleReject} className="btn btn-danger" disabled={submitting} data-testid="reject-btn">Reject</Button>
              <Button onClick={handleApprove} className="btn btn-primary" disabled={submitting} data-testid="approve-btn">Approve</Button>
            </div>
          </div>
        ) : (
          <div style={{ borderTop: "1px solid rgba(96,240,248,0.15)", paddingTop: "1rem", fontSize: "0.85rem", color: "var(--octopus-voltage)" }}>
            {data.reviewed_via === "email" ? (
              <div data-testid="reviewed-via-email-guard" style={{ padding: "0.65rem 0.85rem", background: "rgba(96,240,248,0.08)", border: "1px solid rgba(96,240,248,0.2)", borderRadius: "6px", marginBottom: "0.85rem", color: "var(--octopus-ice)" }}>
                This submission was reviewed by <strong>{data.reviewed_by}</strong> on {new Date(data.reviewed_at).toLocaleString()} via email.
              </div>
            ) : (
              <>
                {data.status === "approved" ? "Approved" : "Rejected"} by <strong>{data.reviewed_by}</strong> on {new Date(data.reviewed_at).toLocaleString()}
              </>
            )}
            {data.review_reason && <><br /><strong>Reason:</strong> {data.review_reason}</>}

            {(data.status === "approved" || data.status === "auto_approved") && (
              <SystemConfirmations
                submission={data}
                onUpdate={(patch) => setData(d => ({ ...d, ...patch }))}
              />
            )}

            <div style={{ marginTop: "1rem", display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              {data.status === "approved" && (
                <Button
                  onClick={() => window.location.href = `${API}/portal/admin/submissions/${submissionId}/csv`}
                  className="btn btn-secondary"
                  data-testid="download-approved-csv-btn"
                >
                  <Download size={12} style={{ marginRight: "0.3rem" }} /> Download approved CSV
                </Button>
              )}
              <Button onClick={onClose} className="btn btn-secondary">Close</Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}


/** Two locked-once buttons for the records updater to confirm that the
 *  approved prices have been entered in WMS and Kraken. After confirmation,
 *  the button is replaced with a "confirmed by X at Y" status row.
 */
function SystemConfirmations({ submission, onUpdate }) {
  const [busy, setBusy] = useState(null); // "wms" | "kraken" | null

  const confirm = async (system) => {
    setBusy(system);
    try {
      const res = await axios.post(
        `${API}/portal/admin/submissions/${submission.id}/confirm-system`,
        { system },
      );
      onUpdate({
        [`${system}_confirmed_at`]: res.data.confirmed_at,
        [`${system}_confirmed_by`]: res.data.confirmed_by,
      });
      toast.success(`Confirmed in ${system.toUpperCase()}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Confirmation failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="system-confirmations" style={{
      marginTop: "1rem", padding: "0.85rem 1rem",
      background: "rgba(10,18,42,0.5)", border: "1px solid rgba(96,240,248,0.15)",
      borderRadius: "6px",
    }}>
      <p style={{ margin: "0 0 0.65rem", color: "var(--octopus-voltage)", fontSize: "0.7rem", letterSpacing: "0.06em", textTransform: "uppercase" }}>
        Records updater · Confirm prices entered
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "0.75rem" }}>
        <ConfirmTile
          label="WMS"
          icon={<Boxes size={14} />}
          confirmedAt={submission.wms_confirmed_at}
          confirmedBy={submission.wms_confirmed_by}
          busy={busy === "wms"}
          onClick={() => confirm("wms")}
        />
        <ConfirmTile
          label="Kraken"
          icon={<Zap size={14} />}
          confirmedAt={submission.kraken_confirmed_at}
          confirmedBy={submission.kraken_confirmed_by}
          busy={busy === "kraken"}
          onClick={() => confirm("kraken")}
        />
      </div>
    </div>
  );
}


function ConfirmTile({ label, icon, confirmedAt, confirmedBy, busy, onClick }) {
  const done = !!confirmedAt;
  return (
    <div data-testid={`confirm-tile-${label.toLowerCase()}`} style={{
      padding: "0.7rem 0.85rem", borderRadius: "6px",
      background: done ? "rgba(34,197,94,0.08)" : "rgba(96,240,248,0.04)",
      border: `1px solid ${done ? "rgba(34,197,94,0.35)" : "rgba(96,240,248,0.2)"}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: done ? "0.35rem" : "0.5rem" }}>
        <span style={{ color: done ? "#86efac" : "var(--octopus-ice)", display: "inline-flex", alignItems: "center", gap: "0.35rem", fontWeight: 600, fontSize: "0.9rem" }}>
          {done ? <CheckCircle2 size={14} /> : icon}
          {label}
        </span>
      </div>
      {done ? (
        <div style={{ fontSize: "0.75rem", color: "var(--octopus-voltage)" }}>
          <div>Confirmed by <strong style={{ color: "var(--octopus-ice)" }}>{confirmedBy}</strong></div>
          <div style={{ opacity: 0.75 }}>{new Date(confirmedAt).toLocaleString()}</div>
        </div>
      ) : (
        <Button
          onClick={onClick}
          disabled={busy}
          className="btn btn-primary"
          data-testid={`confirm-${label.toLowerCase()}-btn`}
          style={{ padding: "0.35rem 0.75rem", fontSize: "0.8rem", width: "100%" }}
        >
          {busy ? "Confirming…" : `Mark entered in ${label}`}
        </Button>
      )}
    </div>
  );
}
